import { useMemo } from 'react';

import HighchartsReact from 'highcharts-react-official';
import { useYearQuarterNavigation } from 'hooks';
import Highcharts from 'lib/highcharts';
import { camelCase, lowerCase } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import { Quantity } from '../types';

import { FunctionColor } from 'components/enums';

import { GroupBy, Quantities } from '../constants';

import { getPeriods } from '../utils';
import { getLocalizedMonthNames } from 'utils/date';
import { displayRoundedNumber } from 'utils/misc';

import { compareWithAtom, groupByAtom } from '../atoms';

import { SkeletonWithIcon } from 'components/shared';

type Series = {
  name: string;
  color: string;
  data: number[];
  stack?: string;
};

type Props = {
  quantities: Quantity[];
  shouldStack: boolean;
  isLoading: boolean;
};

const monthNames = getLocalizedMonthNames();

export default function Chart({ quantities, shouldStack, isLoading }: Props) {
  const { t } = useTranslation('common');
  const xPeriods = getPeriods(quantities);

  const compareWith = useRecoilValue(compareWithAtom);

  const groupBy = useRecoilValue(groupByAtom);
  const isGrouped = !!groupBy;

  const { isViewPerWeek } = useYearQuarterNavigation();

  const options = useMemo(() => {
    let series: Series[] = [];
    if (!shouldStack) {
      series = quantities.map(({ name, totals }) => {
        const nameKey = camelCase(lowerCase(name));
        return {
          name: !compareWith
            ? t(`analyzer.filters.${nameKey}`)
            : `${t(`analyzer.filters.${nameKey}`)} ${t(
                'comparedTo'
              )} ${lowerCase(
                t(`analyzer.filters.${camelCase(lowerCase(compareWith))}`)
              )}`,
          color: FunctionColor[name],
          data: totals.map(({ total }) => total).slice(0, -1)
        };
      });
    } else {
      series = quantities.flatMap(({ name: stack, data }) =>
        data.map(
          ({
            category,
            periods,
            functionDescription,
            functionName,
            functionColor
          }) => {
            const fd = functionDescription || functionName;
            let name =
              groupBy === GroupBy.CATEGORY
                ? category
                  ? t(`dhtCategories.${camelCase(lowerCase(category))}`)
                  : undefined
                : fd;

            let color = functionColor || FunctionColor.DEFAULT;

            if (!name) {
              color = FunctionColor.UNKNOWN;
            }

            if (stack === Quantities.WORK_DEMAND) {
              color = FunctionColor[stack];
            }

            if (groupBy === GroupBy.CATEGORY) {
              color = FunctionColor[category || 'UNKNOWN'];
            }

            if (!groupBy && stack !== Quantities.ROSTER) {
              name = t(`analyzer.filters.${camelCase(lowerCase(stack))}`);
              color = FunctionColor[stack];
            }

            return {
              name: name || t('unknown'),
              stack: t(`analyzer.filters.${camelCase(lowerCase(stack))}`),
              color,
              data: periods.map(({ hours }) => hours)
            };
          }
        )
      );
    }

    const options = {
      title: {
        text: null
      },
      chart: {
        type: 'column',
        numberFormatter(value) {
          return displayRoundedNumber(value);
        }
      },
      credits: {
        enabled: false
      },
      plotOptions: {
        column: {
          stacking: shouldStack ? 'normal' : undefined
        }
      },
      legend: {
        enabled: false
      },
      xAxis: {
        categories: isViewPerWeek
          ? xPeriods.map((p) => `${t('week')} ${p.periodNr}`)
          : monthNames,
        crosshair: true
      },
      yAxis: [
        {
          title: {
            text: t('hours')
          }
        },
        {
          linkedTo: 0,
          title: {
            text: t('hours')
          },
          opposite: true
        }
      ],
      tooltip: {
        ...(isGrouped && {
          pointFormat:
            '<b>{series.userOptions.stack}</b>' +
            '<br/><span style="color:{point.color}">  ●</span><span> {series.name}</span>: {point.y:,.0f}<br/>' +
            'Total: {point.stackTotal:,.0f}'
        })
      },
      series
    };

    return options;
  }, [groupBy, quantities]);

  return (
    <div className="chart-wrapper">
      {isLoading ? (
        <SkeletonWithIcon height={400} />
      ) : (
        <HighchartsReact highcharts={Highcharts} options={options} />
      )}
    </div>
  );
}
